import { Suspense, useMemo } from "react";
import {
  Autocomplete,
  CircularProgress,
  Grid2 as Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Square } from "@mui/icons-material";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { V2SiteSummary } from "./V2SiteSummary";
import { useV2Sites, formatV2SiteLists, FormattedV2Site } from "./V2SitesAPI";
import { PAGES } from "../constants";

export const colors = {
  RED: "#e15759",
  RED_LIGHT: "#f79a9a",
  GREEN: "#006D3B",
  GREEN_LIGHT: "#92d18b",
  YELLOW: "#C5932C",
  YELLOW_LIGHT: "#f9e79f",
  GREY: "#bab0ab",
  DARK_GREY: "#666666",
};

export function V2Reporting() {
  return (
    <Suspense fallback={<CircularProgress />}>
      <V2ReportingBody />
    </Suspense>
  );
}

function V2ReportingBody() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  
  // V2 API data (auto-fetches and caches via React Query)
  const { sites: v2Sites, loading: v2Loading, error: v2Error } = useV2Sites();

  // Format V2 sites
  const allSites = useMemo(() => formatV2SiteLists(v2Sites), [v2Sites]);
    
  const selectedSite = useMemo<FormattedV2Site | null>(() => {
    if (!siteId) return null;
    if (siteId === "all") {
      return allSites.find((s) => s._id === "all" || s.Name === "All") || null;
    }
    return allSites.find((s: FormattedV2Site) => s._id === siteId) || null;
  }, [siteId, allSites]);



  return (
    <Stack alignItems={"center"} spacing={4}>
      <Typography variant="h2" sx={{ width: "100%", textAlign: "center" }}>
        Site Status (V2 API)
      </Typography>

      <Autocomplete
        value={selectedSite}
        onChange={(_, v) => {
          if (v) {
            navigate(`${PAGES.QUANTIFIER}/${v._id}`);
          } else {
            navigate(`${PAGES.QUANTIFIER}`);
          }
        }}
        options={allSites}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(opt, val) => opt._id === val._id}
        fullWidth
        renderInput={(params) => <TextField {...params} label={"Site"} />}
        sx={{ maxWidth: "1000px" }}
        loading={v2Loading}
      />

      {v2Error && (
        <Typography color="error" variant="body2">
          Error loading sites: {v2Error}
        </Typography>
      )}

      <Grid
        container
        spacing={2}
        justifyContent={"space-around"}
        sx={{ maxWidth: "1000px" }}
      >
        <Grid>
          <Typography variant="subtitle2">
            <Square htmlColor={colors.GREEN} />
            Working as Intended
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="subtitle2">
            <Square htmlColor={colors.YELLOW} />
            No Reports within 1 Week
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="subtitle2">
            <Square htmlColor={colors.RED} />
            Not Working/No Reports within 1 Month
          </Typography>
        </Grid>
      </Grid>
      
      <Suspense fallback={<CircularProgress />}>
        {selectedSite && selectedSite.Name === "All"
          ? allSites
              .filter((item: FormattedV2Site) => item.Name !== "All")
              .map((item: FormattedV2Site, idx: number) => (
                <V2SiteSummary siteId={item._id} key={`${idx}-${item._id}`} />
              ))
          : <Outlet />
        }
      </Suspense>
    </Stack>
  );
}
